import { Color, Spacing } from '@persona-id/design-tokens';
import { isEqual } from 'lodash';
import { Fragment, useEffect, useState, VFC } from 'react';
import { BORDER_RADIUSES } from 'shared/styles/variables';
import styled from 'styled-components';

import MaximizablePanel from 'dashboard/components-lib/MaximizablePanel';
import { DynamicFilterSet } from 'dashboard/models/metrics-verification';
import { Trigger } from 'dashboard/models/trigger';
import { FilterRulesForType } from 'dashboard/redux/slices/metrics-verification';
import AndRulesSection from 'dashboard/views/DashboardVerificationAnalyticsVolumeView/AndRulesSection';
import { FilterRule } from 'dashboard/views/DashboardVerificationAnalyticsVolumeView/BaseVerificationFilterRow';
import {
  FilterDimensions,
  FilteredVerificationType,
} from 'dashboard/views/DashboardVerificationAnalyticsVolumeView/VerificationFilteredMetrics';
import Button from 'design-system/components/Button';
import Spacer from 'design-system/components/Spacer';
import { VerificationType } from 'lib/interfaces/api';

const BUTTON_WIDTH = 80;
const OR_BUTTON_WIDTH = 96;

const Line = styled('hr')`
  overflow: visible; /* For IE */
  padding: 0;
  border: none;
  border-top: solid ${Color.CoreSlate200};
  text-align: center;
  border-top-width: thin;
`;

const StyledOrButton = styled(Button)`
  width: ${OR_BUTTON_WIDTH}px;
  height: ${Spacing[6]};
`;

const Well = styled.div`
  background-color: ${Color.CoreSlate50};
  border-radius: ${BORDER_RADIUSES.rounded};
  padding: ${Spacing[2]} ${Spacing[4]};
`;

const StyledButton = styled(Button)`
  background-color: ${Color.CoreSlate200};
  width: ${Spacing[7]};
  height: ${Spacing[5]};
`;

const StyledPanel = styled(MaximizablePanel)`
  min-height: 1200px;
`;

const ApplyButton = styled(Button)`
  background-color: ${Color.CorePurple700};
  color: ${Color.CoreSlate50};
  width: ${BUTTON_WIDTH}px;
  height: ${Spacing[6]};
  float: right;
`;

const StyledCancelButton = styled(Button)`
  width: ${BUTTON_WIDTH}px;
  height: ${Spacing[6]};
  margin-right: ${Spacing[4]};
  float: right;
`;

export const FilterDivider = styled.div`
  margin-top: ${Spacing[4]};
  padding-top: 2px;
  border-top: 1px solid ${({ theme }) => theme.color.border.primary};
  width: 100%;
`;

interface FilterPanelProps {
  panelTitle: string;
  trigger?: Trigger;
  addAndRule: (type: FilteredVerificationType, orIndex: number) => void;
  deleteAndRule: (type: FilteredVerificationType, orIndex: number, andRuleIndex: number) => void;
  addOrRule: (type: FilteredVerificationType) => void;
  updateAndRule: (
    type: FilteredVerificationType,
    orRuleIndex: number,
    andRuleIndex: number,
    dimension?: FilterDimensions,
    operator?: string,
    values?: string[],
  ) => void;
  verificationType: FilteredVerificationType;
  filterRules: FilterRule[][];
  handleOpenDropdown: () => void;
  handleCloseDropdown: () => void;
  handleLoadMetrics: () => void;
  handleCancel: () => void;
  handleBulkRulesUpdate: (filterRules: Record<VerificationType, FilterRulesForType>) => void;
  lastQueryFilterRules: Record<VerificationType, FilterRulesForType>;
  typeDynamicOptions?: DynamicFilterSet;
}

const FilterPanel: VFC<FilterPanelProps> = ({
  panelTitle,
  addOrRule,
  addAndRule,
  deleteAndRule,
  updateAndRule,
  verificationType,
  filterRules,
  handleOpenDropdown,
  handleCloseDropdown,
  handleLoadMetrics,
  handleCancel,
  lastQueryFilterRules,
  handleBulkRulesUpdate,
  typeDynamicOptions,
}) => {
  // If we delete everything, we'll just regenerate a new initial filter
  if (isEqual(filterRules, [[]] as FilterRule[][])) {
    addAndRule(verificationType, 0);
  }

  // Generate a prefix for each orRule's index num to avoid duplication of 'key'
  // When using index in filterRules array, deletion of an element can't trigger re-rendering of contents correctly
  // Initial: [A, B, C] -> Delete: Inital[1] -> Renders [A, B] instead of [A, C] since C gets "1" for key
  // However, this approach doesn't work if we need to swap/sort filterRules
  // See also: https://github.com/persona-id/persona-web/issues/9475#issuecomment-2156076441
  const [[orRuleIndexPrefix, lastLength], setPrefixAndLength] = useState<[number, number]>([0, 0]);

  useEffect(() => {
    if (filterRules.length !== lastLength) {
      setPrefixAndLength([orRuleIndexPrefix + 1, filterRules.length]);
    }
  }, [filterRules]);

  // Used on the new Apply button
  const loadAndClose = () => {
    handleLoadMetrics();
    handleCancel();
  };

  return (
    // our Existing and groups will go here
    <StyledPanel
      data-test={`verifications-filter-panel--${verificationType}`}
      title={panelTitle}
      offsetElementId={`verification-filters-${verificationType}`}
    >
      <>
        <Line />
        <Spacer height={2} />
      </>
      {filterRules.map((andRules, orIndex) => (
        <Fragment key={`$orRule-${orIndex}`}>
          <Well>
            <Spacer height={1} />
            <AndRulesSection
              andRules={andRules}
              orIndex={orIndex}
              deleteAndRule={deleteAndRule}
              updateAndRule={updateAndRule}
              verificationType={verificationType}
              handleOpenDropdown={handleOpenDropdown}
              handleCloseDropdown={handleCloseDropdown}
              typeDynamicOptions={typeDynamicOptions}
            />
            <Spacer height={1} />
            <StyledButton
              onClick={() => addAndRule(verificationType as FilteredVerificationType, orIndex)}
              id={`filter-panel__add-and-rule__${orIndex}`}
              type='button'
              variant='basic'
              width='icon'
            >
              And
            </StyledButton>
            <Spacer height={1} />
          </Well>
          <Spacer height={2} />
        </Fragment>
      ))}
      <Spacer height={2} />
      <StyledOrButton
        type='button'
        variant='basic'
        size='sm'
        width='button'
        onClick={() => addOrRule(verificationType as FilteredVerificationType)}
        id='filter-panel__add-or-rule'
      >
        Or group
      </StyledOrButton>
      <FilterDivider />
      <Spacer height={1} />
      <ApplyButton
        onClick={loadAndClose}
        id='filter-panel__apply'
        data-test='verifications-filter-panel--apply'
        type='button'
        variant='basic'
        size='sm'
        width='icon'
      >
        Apply
      </ApplyButton>
      <StyledCancelButton
        type='button'
        variant='basic'
        size='sm'
        width='button'
        onClick={() => {
          handleCancel();
          handleBulkRulesUpdate(lastQueryFilterRules);
        }}
        id='filter-panel__cancel'
      >
        Cancel
      </StyledCancelButton>
      <Spacer height={2} />
      <Spacer height={2} />
    </StyledPanel>
  );
};

export default FilterPanel;
